import { useMemo } from "react";

type TrendRow = {
  date: string;
  hrv_avg: number | null;
  resting_hr: number | null;
  sleep_duration_hr: number | null;
  sleep_score: number | null;
  recovery_score: number | null;
  source: string | null;
};

type Props = { trend: TrendRow[] };

type MetricKey = "recovery_score" | "hrv_avg" | "sleep_duration_hr" | "resting_hr";

const METRICS: Array<{
  key: MetricKey;
  label: string;
  unit: string;
  color: string;
  // For most metrics higher is better; resting HR is inverted (lower = better)
  higherIsBetter: boolean;
  format: (v: number) => string;
}> = [
  { key: "recovery_score",    label: "Recovery",   unit: "",    color: "#2A9D8F", higherIsBetter: true,  format: (v) => `${Math.round(v)}` },
  { key: "hrv_avg",           label: "HRV",        unit: "ms",  color: "#A8DADC", higherIsBetter: true,  format: (v) => `${Math.round(v)}` },
  { key: "sleep_duration_hr", label: "Sleep",      unit: "h",   color: "#457B9D", higherIsBetter: true,  format: (v) => v.toFixed(1) },
  { key: "resting_hr",        label: "Resting HR", unit: "bpm", color: "#E76F51", higherIsBetter: false, format: (v) => `${Math.round(v)}` },
];

// Build an SVG sparkline path from a series of numbers, normalized to a box.
function sparkPath(values: number[], w: number, h: number, pad = 2): string {
  if (values.length === 0) return "";
  if (values.length === 1) {
    const y = h / 2;
    return `M ${pad} ${y} L ${w - pad} ${y}`;
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const innerW = w - pad * 2;
  const innerH = h - pad * 2;
  return values
    .map((v, i) => {
      const x = pad + (i / (values.length - 1)) * innerW;
      // invert y because SVG y grows downward
      const y = pad + innerH - ((v - min) / range) * innerH;
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
}

export function TrendingCard({ trend }: Props) {
  const hasData = trend && trend.length > 0;

  const metrics = useMemo(() => {
    return METRICS.map((m) => {
      const series = trend
        .map((r) => r[m.key])
        .filter((v): v is number => typeof v === "number" && !isNaN(v));

      const latest = series.length ? series[series.length - 1] : null;
      // Compare the most-recent point against the mean of the prior window
      let deltaPct: number | null = null;
      let direction: "up" | "down" | "flat" = "flat";
      if (series.length >= 3) {
        const recent = series[series.length - 1];
        const prior = series.slice(0, -1);
        const priorMean = prior.reduce((a, b) => a + b, 0) / prior.length;
        if (priorMean !== 0) {
          deltaPct = ((recent - priorMean) / priorMean) * 100;
          if (Math.abs(deltaPct) < 2) direction = "flat";
          else direction = deltaPct > 0 ? "up" : "down";
        }
      }
      // Is the movement "good"? (resting HR up = bad)
      const isGood =
        direction === "flat"
          ? null
          : (direction === "up") === m.higherIsBetter;

      return { ...m, series, latest, deltaPct, direction, isGood };
    });
  }, [trend]);

  return (
    <div className="bg-d_card border border-d_border/20 rounded-[10px] p-3 transition-colors hover:border-d_celeste/10 h-full min-h-0 overflow-hidden flex flex-col">
      <div className="font-serif text-[17px] font-semibold mb-2.5 flex items-center justify-between tracking-[0.01em]">
        Trending
        <span className="text-[10px] tracking-[1.8px] uppercase text-d_text3 font-semibold">Last 14 days</span>
      </div>

      {!hasData ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center gap-1 py-4">
          <div className="text-[13px] text-d_text2 font-medium">No trend data yet</div>
          <div className="text-[11px] text-d_text3 leading-snug max-w-[260px]">
            Connect a wearable and sync to see how your recovery, HRV, sleep, and resting heart rate move over time.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-2 flex-1 min-h-0">
          {metrics.map((m) => {
            const arrow = m.direction === "up" ? "↑" : m.direction === "down" ? "↓" : "→";
            const arrowColor =
              m.isGood === null ? "var(--text3-rgb)" : m.isGood ? "#2A9D8F" : "#E76F51";
            return (
              <div
                key={m.key}
                className="flex flex-col rounded-lg border border-d_border/20 bg-d_card2/40 px-2.5 py-2 min-w-0"
              >
                <div className="flex items-center justify-between gap-1">
                  <span className="text-[10px] tracking-[0.06em] uppercase text-d_text3 font-semibold truncate">{m.label}</span>
                  <span
                    className="text-[12px] font-bold leading-none flex-shrink-0"
                    style={{ color: m.isGood === null ? undefined : arrowColor }}
                  >
                    {arrow}
                  </span>
                </div>

                <div className="flex items-baseline gap-1 mt-1">
                  <span className="font-serif text-[28px] font-bold leading-none tabular-nums text-d_text">
                    {m.latest != null ? m.format(m.latest) : "—"}
                  </span>
                  {m.unit && m.latest != null && (
                    <span className="text-[10px] text-d_text3 font-medium">{m.unit}</span>
                  )}
                </div>

                {m.deltaPct != null && (
                  <div
                    className="text-[10px] font-semibold mt-0.5"
                    style={{ color: m.isGood === null ? undefined : arrowColor }}
                  >
                    {m.deltaPct > 0 ? "+" : ""}{m.deltaPct.toFixed(0)}% vs avg
                  </div>
                )}

                {/* Sparkline */}
                <div className="mt-auto pt-1.5">
                  <svg viewBox="0 0 100 28" preserveAspectRatio="none" className="w-full h-7 block">
                    {/* baseline */}
                    <line x1="0" y1="27" x2="100" y2="27" style={{ stroke: "rgb(var(--text3) / 0.15)" }} strokeWidth={0.5} />
                    {m.series.length > 0 && (
                      <>
                        {/* area fill under the line */}
                        <path
                          d={`${sparkPath(m.series, 100, 28)} L 98 26 L 2 26 Z`}
                          fill={m.color}
                          opacity={0.08}
                        />
                        <path
                          d={sparkPath(m.series, 100, 28)}
                          fill="none"
                          stroke={m.color}
                          strokeWidth={1.5}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        {/* last point dot */}
                        {(() => {
                          const vals = m.series;
                          const min = Math.min(...vals);
                          const max = Math.max(...vals);
                          const range = max - min || 1;
                          const x = vals.length === 1 ? 98 : 2 + ((vals.length - 1) / (vals.length - 1)) * 96;
                          const y = 2 + 24 - ((vals[vals.length - 1] - min) / range) * 24;
                          return <circle cx={x} cy={y} r={1.8} fill={m.color} />;
                        })()}
                      </>
                    )}
                  </svg>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
